import { useMemo, useState } from "react"
import type { DateTime } from "luxon"

import { RefetchIntervals } from "../../../../../core/api/refetchIntervals"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useClock } from "../../../../../shared/hooks/useClock"
import { useDateTime } from "../../../../localization/useDateTime"
import { useCurrentServiceCenter } from "../../../store/serviceCenterHooks"

import { fetchResourceOccupancy, MeetingRoomsQuery } from "../meetingRoomsApi"
import {
    buildExportRows,
    buildFreeHourGroups,
    buildHourGroups,
    buildHourOccupancy,
    buildRoomGroups,
    buildRoomsAtHour,
    countStatuses,
    filterRows,
    filterRowsForDay,
    getDayEndMillis,
    getDayStartMillis,
    DEFAULT_TABLE_PAGE_SIZE,
    getTablePagination,
    OVERVIEW_FIRST_HOUR,
    OVERVIEW_LAST_HOUR,
    selectMeetingRoomsOverview,
    toHourWindow,
} from "../meetingRoomsLib"
import { ALL_FLOORS, MeetingRoomStatusFilter, MeetingRoomsView } from "../meetingRoomsTypes"
import type { MeetingRoomRow } from "../meetingRoomsTypes"

const EMPTY_ROOMS: ReadonlyArray<MeetingRoomRow> = []

const EMPTY_MESSAGES: ReadonlyArray<string> = []

const EMPTY_FLOOR_NAMES: ReadonlyArray<string> = []

function toInitialHour(now: DateTime) {
    return now.hour >= OVERVIEW_FIRST_HOUR && now.hour <= OVERVIEW_LAST_HOUR ? now.hour : OVERVIEW_FIRST_HOUR
}

export function useMeetingRoomsOverview() {
    const { serviceCenterId } = useCurrentServiceCenter()
    const dateTimeFactory = useDateTime()
    // Minute resolution: "in use now" and the current hour only change on a minute boundary.
    const { clock: now } = useClock({ resolution: "minute" })

    const [date, setDate] = useState<DateTime>(() => dateTimeFactory.now().startOf("day"))
    const [view, setView] = useState(MeetingRoomsView.STATUS)
    const [selectedHour, setSelectedHour] = useState<number>(() => toInitialHour(dateTimeFactory.now()))
    const [statusFilter, setStatusFilter] = useState(MeetingRoomStatusFilter.ALL)
    const [floor, setFloor] = useState(ALL_FLOORS)
    const [search, setSearch] = useState("")
    const [tablePageIndex, setTablePageIndex] = useState(0)
    const [tablePageSize, setTablePageSize] = useState(DEFAULT_TABLE_PAGE_SIZE)

    const isoDate = date.toISODate() ?? ""
    const roomsQuery = useApiQuery(fetchResourceOccupancy, {
        queryName: MeetingRoomsQuery.RESOURCE_OCCUPANCY,
        dependencies: { serviceCenterId, date: isoDate },
        select: selectMeetingRoomsOverview,
        refetchInterval: RefetchIntervals.short,
    })

    const rooms = roomsQuery.data?.rooms ?? EMPTY_ROOMS
    const summaryMessages = roomsQuery.data?.summaryMessages ?? EMPTY_MESSAGES
    const nowMillis = now.toMillis()
    const isToday = date.hasSame(now, "day")
    const dayStartMillis = getDayStartMillis(date)
    const dayEndMillis = getDayEndMillis(date)
    const hourWindow = useMemo(() => toHourWindow(date, selectedHour), [date, selectedHour])

    const floorNames = roomsQuery.data?.floorNames ?? EMPTY_FLOOR_NAMES
    const hourOccupancy = useMemo(() => buildHourOccupancy(rooms, date), [rooms, date])
    const roomsAtHour = useMemo(
        () => buildRoomsAtHour(rooms, hourWindow, dayEndMillis, nowMillis, isToday),
        [rooms, hourWindow, dayEndMillis, isToday, nowMillis],
    )
    const tableRows = useMemo(
        () => filterRows(roomsAtHour, { statusFilter, floor, search }),
        [roomsAtHour, statusFilter, floor, search],
    )
    const statusCounts = useMemo(() => countStatuses(roomsAtHour), [roomsAtHour])
    const dayRows = useMemo(
        () => filterRowsForDay(roomsAtHour, { statusFilter, floor, search }),
        [roomsAtHour, statusFilter, floor, search],
    )
    const hourGroups = useMemo(
        () =>
            statusFilter === MeetingRoomStatusFilter.AVAILABLE
                ? buildFreeHourGroups(roomsAtHour, date, { floor, search })
                : buildHourGroups(dayRows, date, nowMillis),
        [statusFilter, roomsAtHour, dayRows, date, nowMillis, floor, search],
    )
    const roomGroups = useMemo(() => buildRoomGroups(dayRows, nowMillis), [dayRows, nowMillis])
    const exportRows = useMemo(
        () => buildExportRows(view, { tableRows, hourGroups, roomGroups }, nowMillis),
        [view, tableRows, hourGroups, roomGroups, nowMillis],
    )

    const tablePagination = getTablePagination(tableRows.length, tablePageIndex, tablePageSize)

    const changeStatusFilter = (value: MeetingRoomStatusFilter) => {
        setStatusFilter(value)
        setTablePageIndex(0)
    }
    const changeFloor = (value: string) => {
        setFloor(value)
        setTablePageIndex(0)
    }
    const changeSearch = (value: string) => {
        setSearch(value)
        setTablePageIndex(0)
    }
    const changeTablePageSize = (value: number) => {
        setTablePageSize(value)
        setTablePageIndex(0)
    }
    const resetFilters = () => {
        setStatusFilter(MeetingRoomStatusFilter.ALL)
        setFloor(ALL_FLOORS)
        setSearch("")
        setTablePageIndex(0)
    }

    return {
        roomsQuery,
        date,
        setDate,
        view,
        setView,
        selectedHour,
        setSelectedHour,
        statusFilter,
        setStatusFilter: changeStatusFilter,
        floor,
        setFloor: changeFloor,
        search,
        setSearch: changeSearch,
        resetFilters,
        rooms,
        summaryMessages,
        nowMillis,
        isToday,
        currentHour: isToday ? now.hour : undefined,
        dayStartMillis,
        dayEndMillis,
        hourWindow,
        floorNames,
        hourOccupancy,
        tableRows,
        tablePagination,
        setTablePageIndex,
        setTablePageSize: changeTablePageSize,
        exportRows,
        statusCounts,
        hourGroups,
        roomGroups,
    }
}
